import { useEffect, useState } from "react";
import { ArrowUpRight } from "assets/ArrowUpRight";
import { ShootingStars } from "core/components/global/background/stars/shooting-stars";
import { StarsBackground } from "core/components/global/background/stars/stars-background";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import { RedirectInformationBlock } from "./siteRedirect.styled";

export default function SiteRedirect() {
  const { url } = useParams();
  const navigate = useNavigate();
  const [decodedUrl, setDecodedUrl] = useState("");
  const [countdown, setCountdown] = useState(3);
  const [isProcessing, setIsProcessing] = useState(true);
  const [redirectInProgress, setRedirectInProgress] = useState(true);

  // Decode URL when component mounts
  useEffect(() => {
    if (url) {
      try {
        const decoded = decodeURIComponent(url);
        setDecodedUrl(decoded);

        // Extract domain for display
        const domain = new URL(decoded).hostname;
        document.title = `Redirecting to ${domain}`;
      } catch (error) {}
    }
  }, [url, navigate]);

  // Set up countdown and redirect logic
  useEffect(() => {
    if (decodedUrl && redirectInProgress) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Perform the redirect after countdown
            window.open(decodedUrl, "_blank", "noopener noreferrer");
            // Show the new message
            setIsProcessing(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [decodedUrl, redirectInProgress]);

  // Cancel redirect and go back
  const handleCancel = () => {
    setRedirectInProgress(false);
  };

  // Extract domain name for display
  const displayDomain = decodedUrl
    ? new URL(decodedUrl).hostname
    : "external site";

  return (
    <div
      className="h-100 bg-neutral-900 flex flex-col items-center justify-center relative w-full"
      onClick={handleCancel}
    >
      <div className="relative flex-col md:flex-row z-10 text-3xl md:text-5xl md:leading-tight max-w-5xl mx-auto text-center tracking-tight font-medium bg-clip-text text-transparent bg-gradient-to-b from-neutral-800 via-white to-white flex items-center gap-2 md:gap-8">
        <RedirectInformationBlock className="p-8 rounded-lg flex flex-col items-center justify-center text-center">
          <div className="relative w-16 h-16 mb-4">
            <div className={`absolute inset-0 border-t-4 border-blue-500 rounded-full ${(redirectInProgress && isProcessing) && "animate-spin" }`}></div>
          </div>
          <h3 className="text-lg font-semibold mb-2 text-white">Opening External Site</h3>
          {isProcessing ? (
            <p className="text-sm text-white">
              Redirecting to {displayDomain} in {countdown} seconds...
            </p>
          ) : (
            <p className="text-sm text-white">
              Your page should have opened a new tab to {displayDomain}
            </p>
          )}
          <div className="flex gap-3 mt-6">
            <Link
              className="MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeMedium MuiButton-textSizeMedium MuiButton-colorPrimary MuiButton-root MuiButton-text MuiButton-textPrimary MuiButton-sizeMedium MuiButton-textSizeMedium MuiButton-colorPrimary px-4 py-2 text-white rounded-md hover:bg-blue-600 css-f7iyy0-MuiButtonBase-root-MuiButton-root"
              to={decodedUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <p className="text-white">Continue Now</p>
            </Link>
            <Button
              className="px-4 py-2 border-gray-100 text-white rounded-md hover:bg-red-800"
              onClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
            >
              Cancel
            </Button>
          </div>
        </RedirectInformationBlock>
      </div>
      <ShootingStars />
      <StarsBackground />
    </div>
  );
}
